/**
 * Redux Toolkit slice for user profile settings, settings lookup filters, and
 * profile update activity.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import request from '../../services/request';
import pmoDecFieldProperties from '../../constants/fieldProperties/pmoDecFieldProperties';
import { searchPanelDataActions } from '../searchPanelData/searchPanelDataSlice';
import getflattenResponse, { getEndpointURL, cryptoRand } from '../../helpers/common';
import { getFilterValuesFromResponse, mapFiltersToRequest } from '../../helpers/dataHelpers';

const name = 'ProfileSetting';
function createInitialState() {
  return {
    DateFormat: 'MM/dd/yyyy',
    loadingSpinner: false,
    savingSpinner: false,
    userProfile: [],
    filterDropdownValues: {
      factoryCode: ['TX']
    },
    selectedFilters: {
      factoryCode: [],
      pmodec: [],
      nikeId: [],
      emailId: [],
      analystName: []
    },
    settingsFilterId: 'none',
    SettingsData: [],
    SearchRequestBody: null,
    updatedProfileSettings: false
  };
}

/**
 * Refreshes the settings filter identifier used to trigger UI updates.
 */
function setSettingFilterId(state) {
  return { ...state, settingsFilterId: cryptoRand().toString().slice(2, 10) };
}

/**
 * Stores settings filter dropdown values when the API returns filter metadata.
 */
function settingUpdateFilterDropDownValue(state, action) {
  // Ignore empty filter metadata responses so existing dropdown values remain available.
  if (Object.keys(action.payload ?? {}).length > 0) {
    return {
      ...state,
      filterDropdownValues: action.payload
    };
  }
  return { ...state };
}

/**
 * Stores flattened settings search results.
 */
function settingSearchData(state, action) {
  return {
    ...state,
    SettingsData: action.payload?.objects
  };
}

/**
 * Stores selected settings lookup filter values by filter key.
 */
function addSettingFilterValues(state, action) {
  // Only valid filter payloads update the keyed selectedFilters state.
  if (Array.isArray(action.payload?.values) && action.payload?.filter) {
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        [action.payload.filter]: action.payload.values
      }
    };
  }
  return { ...state };
}

/**
 * Updates the settings search request body with mapped filters.
 */
function updateSettingFilterInRequest(state, action) {
  const updatedBody = { ...state.body, filter: action.payload };
  return { ...state, SearchRequestBody: updatedBody };
}

/**
 * Stores the full settings search request body.
 */
function updateSettingSearchRequestBody(state, action) {
  return {
    ...state,
    SearchRequestBody: action.payload
  };
}

/**
 * Updates the preferred date format in settings state.
 */
function dateFormat(state, action) {
  return {
    ...state,
    DateFormat: action.payload
  };
}

/**
 * Stores user profile data in settings state.
 */
function userProfile(state, action) {
  return {
    ...state,
    userProfile: action.payload
  };
}

/**
 * Marks settings lookup loading as active.
 */
function toggleSettingSpinner(state) {
  return {
    ...state,
    loadingSpinner: true
  };
}

/**
 * Marks settings lookup loading as complete.
 */
function toggleSettingSpinnerSuccess(state) {
  return {
    ...state,
    loadingSpinner: false
  };
}

/**
 * Marks profile settings save activity as active.
 */
function toggleSavingSettingSpinner(state) {
  return {
    ...state,
    savingSpinner: true
  };
}

/**
 * Marks profile settings save activity as complete.
 */
function toggleSavingSettingSpinnerSuccess(state) {
  return {
    ...state,
    savingSpinner: false
  };
}

/**
 * Collects reducers that manage profile settings state.
 */
function createReducers() {
  return {
    setSettingFilterId,
    settingUpdateFilterDropDownValue,
    settingSearchData,
    addSettingFilterValues,
    updateSettingFilterInRequest,
    updateSettingSearchRequestBody,
    dateFormat,
    userProfile,
    toggleSettingSpinner,
    toggleSettingSpinnerSuccess,
    toggleSavingSettingSpinner,
    toggleSavingSettingSpinnerSuccess
  };
}

const initialState = createInitialState();
const reducers = createReducers();
const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };
/**
 * Builds filtersInResponse metadata for a settings lookup filter.
 */
const filterFunc = (filter) => {
  const filterInResponse = {};
  // Field properties define which response fields populate dependent filters.
  const fieldProperties = pmoDecFieldProperties[filter];
  if (!fieldProperties?.primary) {
    return null;
  }
  filterInResponse.primary = fieldProperties.primary;
  if (fieldProperties.secondary) {
    filterInResponse.secondary = fieldProperties.secondary;
  }
  return filterInResponse;
};

/**
 * Creates a thunk that saves profile settings through updateUserProfile.
 */
function userProfileSettings() {
  return createAsyncThunk(
    `${name}/userProfileSettings`,
    async (props, { getState, dispatch }) => {
      dispatch(actions.toggleSavingSettingSpinner());
      const { requestBody, callback } = props;
      request({
        api: 'updateUserProfile',
        method: 'put',
        data: requestBody
      },
      dispatch,
      getState).then((response) => {
        dispatch(actions.toggleSavingSettingSpinnerSuccess());
        if (response) {
          dispatch(actions.userProfile([response.data]));
          // The updated profile setting key determines which filter or column order to refresh.
          const callerType = Object.keys(requestBody ?? {})[0];
          const callerTypes = [
            {
              filter: 'selectedFilterListForPPMReport',
              column: 'selectedColumnListForPPMReport'
            },
            {
              filter: 'selectedFilterListForLineItemReport',
              column: 'selectedColumnListForLineItemReport'
            },
            {
              filter: 'selectedFilterListForPOSearch',
              column: 'selectedColumnListForPOSearch'
            }
          ];
          let filterValue = '';
          let columnValue = '';
          // Report-specific settings share paired filter and column preference keys.
          const matchedCallerType = callerTypes
            .find(({ filter, column }) => callerType?.includes(filter)
              || callerType?.includes(column));
          if (matchedCallerType) {
            filterValue = matchedCallerType.filter;
            columnValue = matchedCallerType.column;
          } else if (callerType?.includes('selectedColumnList')) {
            columnValue = callerType;
          } else if (callerType?.includes('selectedFilter')) {
            filterValue = callerType;
          }
          dispatch(searchPanelDataActions.getFilterAndColumnOrderList({
            columnType: columnValue, filterType: filterValue
          }));
          if (callback) {
            callback(response);
          }
        }
      })
        .catch((error) => {
          dispatch(actions.toggleSettingSpinnerSuccess());
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that initializes a user profile through CREATEUSERPROFILE.
 */
function createUserProfile() {
  return createAsyncThunk(
    `${name}/createUserProfile`,
    async (callback, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'CREATEUSERPROFILE',
        method: 'post'
      },
      dispatch,
      getState).then((response) => {
        if (callback) {
          callback(response);
        }
      })
        .catch((error) => {
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that loads user profile data through getUserProfile.
 */
function getUserProfile() {
  return createAsyncThunk(
    `${name}/getUserProfile`,
    async ({ callback }, { getState, dispatch }) => {
      dispatch(actions.toggleSettingSpinner());
      request({
        api: 'getUserProfile',
        method: 'get'
      },
      dispatch,
      getState).then((response) => {
        dispatch(actions.toggleSettingSpinnerSuccess());
        dispatch(actions.userProfile(response.data?.objects));
        if (callback) {
          callback(response);
        }
      })
        .catch((error) => {
          dispatch(actions.toggleSettingSpinnerSuccess());
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that applies settings lookup filters through SettingsSearch.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async (props, { getState, dispatch }) => {
      const { payload, applyFiltersCallback, shouldRequestFilterValues } = props;
      dispatch(toggleSpinner(true));
      dispatch(actions.addSettingFilterValues(payload));
      let state = getState();
      const filtersRequest = mapFiltersToRequest(state.PMODECLookup.selectedFilters);
      dispatch(actions.updateSettingFilterInRequest(filtersRequest));
      state = getState();
      const requestBody = { ...state.PMODECLookup.SearchRequestBody };
      // Request filter metadata only for filters that currently have selected values.
      const selectedFilterKeys = Object.keys(state.PMODECLookup.selectedFilters)
        .filter((filter) => state.PMODECLookup.selectedFilters[filter]);
      if (shouldRequestFilterValues && selectedFilterKeys.length > 0) {
        requestBody.filtersInResponse = selectedFilterKeys.map((filter) => filterFunc(filter))
          .filter(Boolean);
      } else {
        delete requestBody.filtersInResponse;
      }
      request({
        url: getEndpointURL('SettingsSearch'),
        method: 'post',
        data: requestBody
      },
      dispatch,
      getState).then((response) => {
        dispatch(toggleSpinner(false));
        if (response.data?.filters) {
          dispatch(actions.settingUpdateFilterDropDownValue(
            getFilterValuesFromResponse(response.data.filters)
          ));
        }
        dispatch(actions.setSettingFilterId());
        dispatch(actions.updateSettingSearchRequestBody(requestBody));
        dispatch(actions.settingSearchData(getflattenResponse(response.data ?? {})));
        if (applyFiltersCallback) {
          applyFiltersCallback(response);
        }
      })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          if (applyFiltersCallback) {
            applyFiltersCallback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that updates a specific user profile timestamp field.
 */
function createUserProfileTimestampThunk(thunkName, timestampField) {
  return createAsyncThunk(
    `${name}/${thunkName}`,
    async (_, { getState, dispatch }) => {
      dispatch(actions.toggleSavingSettingSpinner());
      request({
        api: 'updateUserProfile',
        method: 'put',
        // Dynamic field name lets one thunk factory update different activity timestamps.
        data: { [timestampField]: new Date() }
      },
      dispatch,
      getState).then(() => {
        dispatch(actions.toggleSavingSettingSpinnerSuccess());
      })
        .catch(() => {
          dispatch(actions.toggleSettingSpinnerSuccess());
        });
    }
  );
}

/**
 * Instantiates async profile settings actions exposed by the slice.
 */
function createExtraActions() {
  return {
    createUserProfile: createUserProfile(),
    getUserProfile: getUserProfile(),
    userProfileSettings: userProfileSettings(),
    applyFilters: applyFilters(),
    userProfileUpdateTime: createUserProfileTimestampThunk('userProfileUpdateTime', 'userLastLoginAt'),
    userProfileUpdateRefreshTime: createUserProfileTimestampThunk(
      'userProfileUpdateRefreshTime',
      'userLastRefreshAt'
    )
  };
}

const extraActions = createExtraActions();

export const settingsActions = { ...actions, ...extraActions };
export const settingsReducer = slice.reducer;
